<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import WootSubmitButton from '../../../../components/buttons/FormSubmitButton.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AttachmentUpload from 'dashboard/components/widgets/AttachmentUpload.vue';

export default {
  name: 'AddCanned',
  components: {
    WootSubmitButton,
    Modal,
    WootMessageEditor,
    AttachmentUpload,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      attachments: [],
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      // Không bắt buộc content nếu có attachments
    },
  },
  computed: {
    hasAttachments() {
      return this.attachments && this.attachments.length > 0;
    },
    isFormValid() {
      // Cho phép form valid nếu có content hoặc attachments
      const hasContent = this.content && this.content.trim().length > 0;
      const hasAttachments = this.hasAttachments;

      return (
        (hasContent || hasAttachments) &&
        !this.v$.shortCode.$invalid &&
        !this.addCanned.showLoading
      );
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachments = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    addCannedResponse() {
      // Show loading on button
      this.addCanned.showLoading = true;

      // Prepare data for API call
      const cannedData = {
        short_code: this.shortCode,
        content: this.content,
      };

      // Add attachments if any
      if (this.hasAttachments) {
        cannedData.attachments = this.attachments;
      }

      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', cannedData)
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
    onAttachmentAdded(files) {
      // Convert FileList to Array and add to attachments
      const newFiles = Array.from(files);
      this.attachments = [...this.attachments, ...newFiles];
    },
    onAttachmentRemoved(index) {
      this.attachments.splice(index, 1);
    },
    onAttachmentError(error) {
      useAlert(error.message || this.$t('CANNED_MGMT.ADD.ATTACHMENT_ERROR'));
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
            <span v-if="v$.shortCode.$error" class="message">
              {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.ERROR') }}
            </span>
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Attachment Upload Section -->
        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.LABEL') }}
            <AttachmentUpload
              :attachments="attachments"
              :max-files="5"
              :max-size="40 * 1024 * 1024"
              :allowed-types="[
                'image/*',
                'video/*',
                'audio/*',
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              ]"
              @attachment-added="onAttachmentAdded"
              @attachment-removed="onAttachmentRemoved"
              @attachment-error="onAttachmentError"
            />
            <div class="attachment-hint">
              {{
                $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.HINT', {
                  maxFiles: 5,
                  maxSize: 40,
                })
              }}
            </div>
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <WootSubmitButton
            :disabled="!isFormValid"
            :button-text="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :loading="addCanned.showLoading"
          />
          <button class="button clear" @click.prevent="onClose">
            {{ $t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
          </button>
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
